import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../../components/PageHeader';
import ContextMenuIconButton from '../../../components/ContextMenuIconButton';
import DeleteContactModal from '../../../components/DeleteContactModal';
import ContactFormModal from '../../../components/ContactFormModal';
import { useBrandContext, BrandContact } from '../../../contexts/BrandContext';
import { PAGE_LOCATIONS, PAGE_ADD_LOCATION, PAGE_ADD_BRAND } from '../../../constants/UriConstants';
import { EditIcon, TrashCanIcon } from '../../../assets/svgs/icons';
import EditBrandInfoModal, { BrandInfoData } from '../../../components/EditBrandInfoModal';
import { getRestaurantsByBrandID, updateBrand, deleteBrand } from '../../../api/brand';

const COLGROUP = (
  <colgroup>
    <col style={{ width: '40px' }} />
    <col style={{ width: '15%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: '17%' }} />
    <col style={{ width: '20%' }} />
    <col style={{ width: '20%' }} />
  </colgroup>
);

const TH_CLASS = 'text-left py-3 text-xs tracking-wider text-[var(--color-text-secondary)] font-normal';

const BrandProfilePage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { currentBrand, setBrand, refreshCurrentBrand, refreshBrands } = useBrandContext();

  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<BrandContact | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<BrandContact | null>(null);
  const [isEditBrandOpen, setIsEditBrandOpen] = useState(false);
  const [isDeleteBrandOpen, setIsDeleteBrandOpen] = useState(false);
  const [locationCount, setLocationCount] = useState<number>(0);

  useEffect(() => {
    if (brandId && currentBrand?.id !== brandId) {
      setBrand(brandId);
    }
  }, [brandId, currentBrand?.id, setBrand]);

  // Fetch location count
  useEffect(() => {
    const fetchLocationCount = async () => {
      if (!currentBrand?.id) return;
      try {
        const locations = await getRestaurantsByBrandID(currentBrand.id);
        setLocationCount(locations?.length || 0);
      } catch (error) {
        console.error('Error fetching location count:', error);
      }
    };
    fetchLocationCount();
  }, [currentBrand?.id]);

  if (!currentBrand) {
    return null;
  }

  const handleAddContact = () => {
    setEditingContact(null);
    setIsContactFormOpen(true);
  };

  const handleEditContact = (contact: BrandContact) => {
    setEditingContact(contact);
    setIsContactFormOpen(true);
  };

  const handleDeleteContact = (contact: BrandContact) => {
    setDeletingContact(contact);
    setIsDeleteModalOpen(true);
  };

  // TODO: Replace with API call to create/update contact
  const handleContactFormSubmit = (data: {
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    email: string;
  }) => {
    setIsContactFormOpen(false);
    setEditingContact(null);
  };

  // TODO: Replace with API call to delete contact
  const handleConfirmDeleteContact = () => {
    setIsDeleteModalOpen(false);
    setDeletingContact(null);
  };

  const handleSaveBrandInfo = async (data: BrandInfoData) => {
    try {
      await updateBrand(currentBrand.id, {
        name: data.brandName || undefined,
        website: data.website || undefined
      });
      await refreshCurrentBrand();
      await refreshBrands();
      toast.success('Brand updated successfully!', {
        position: toast.POSITION.TOP_RIGHT
      });
    } catch (error) {
      console.error('Error updating brand:', error);
      toast.error('Failed to update brand.', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
    setIsEditBrandOpen(false);
  };

  const handleConfirmDeleteBrand = async () => {
    try {
      await deleteBrand(currentBrand.id);
      await refreshBrands();
      navigate('/');
    } catch (error) {
      console.error('Error deleting brand:', error);
    }
    setIsDeleteBrandOpen(false);
  };

  const getBrandInfoData = (): BrandInfoData => ({
    brandName: currentBrand.name,
    cuisineType: currentBrand.cuisine?.name || '',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    website: currentBrand.website || ''
  });

  return (
    <div>
      <PageHeader title="BRAND PROFILE">
        <button
          type="button"
          className="px-6 py-2 rounded-full text-xs text-white transition-colors"
          style={{ backgroundColor: 'var(--color-accent)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
          }}
          onClick={() => navigate(`/${currentBrand.id}/${PAGE_ADD_BRAND}`)}
        >
          ADD BRAND
        </button>
      </PageHeader>

      <div style={{ padding: '24px 50px' }}>
        {/* Brand Info Section */}
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Actions" />
              <th className={TH_CLASS}>NAME</th>
              <th className={TH_CLASS}>ADDRESS</th>
              <th className={TH_CLASS}>PHONE</th>
              <th className={TH_CLASS}>EMAIL</th>
              <th className={TH_CLASS}>WEBSITE</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-6">
                <ContextMenuIconButton
                  itemID={currentBrand.id}
                  onEditClicked={() => () => setIsEditBrandOpen(true)}
                  onDeleteClicked={() => () => setIsDeleteBrandOpen(true)}
                />
              </td>
              <td className="py-6">{currentBrand.name}</td>
              <td className="py-4 whitespace-pre-line">--</td>
              <td className="py-6">--</td>
              <td className="py-6">--</td>
              <td className="py-6">{currentBrand.website || '--'}</td>
            </tr>
          </tbody>
        </table>

        {/* Brand Details Section */}
        <table className="w-full" style={{ tableLayout: 'fixed', marginTop: '48px' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Spacer" />
              <th className={TH_CLASS}>CUISINE</th>
              <th className={TH_CLASS}>LOGO</th>
              <th className={TH_CLASS}>NUMBER OF LOCATIONS</th>
              <th className={TH_CLASS} colSpan={2}>
                LOCATIONS
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td />
              <td className="py-6">{currentBrand.cuisine?.name || '--'}</td>
              <td className="py-6">
                {currentBrand.logoUrl ? (
                  <img src={currentBrand.logoUrl} alt="Brand logo" className="h-12" />
                ) : (
                  <span className="text-[var(--color-text-muted)]">--</span>
                )}
              </td>
              <td className="py-6">{locationCount}</td>
              <td className="py-6" colSpan={2}>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="px-5 py-1.5 rounded-full text-xs text-white transition-colors"
                    style={{ backgroundColor: 'var(--color-primary-blue)' }}
                    onClick={() => navigate(`/${currentBrand.id}/${PAGE_LOCATIONS}`)}
                  >
                    VIEW LOCATIONS
                  </button>
                  <button
                    type="button"
                    className="px-5 py-1.5 rounded-full text-xs text-white transition-colors"
                    style={{ backgroundColor: 'var(--color-accent)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                    }}
                    onClick={() => navigate(`/${currentBrand.id}/${PAGE_ADD_LOCATION}`)}
                  >
                    ADD LOCATION
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Contacts Section */}
        <table className="w-full" style={{ tableLayout: 'fixed', marginTop: '48px' }}>
          {COLGROUP}
          <thead>
            <tr className="border-b border-[var(--color-border-light)]">
              <th aria-label="Spacer" />
              <th className={TH_CLASS}>CONTACT</th>
              <th className={TH_CLASS}>TITLE</th>
              <th className={TH_CLASS}>EMAIL</th>
              <th className={TH_CLASS}>PHONE</th>
              <th className="text-right py-3">
                <button
                  type="button"
                  aria-label="Add contact"
                  onClick={handleAddContact}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-accent)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 'auto'
                  }}
                >
                  <span style={{ color: 'white', fontSize: '20px', lineHeight: 1 }}>+</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentBrand.contacts.map((contact) => (
              <tr key={contact.id}>
                <td />
                <td className="py-6">{contact.name}</td>
                <td className="py-6">{contact.title}</td>
                <td className="py-6">{contact.email}</td>
                <td className="py-6">{contact.phone}</td>
                <td className="py-6">
                  <div className="flex gap-2 justify-start">
                    <button
                      type="button"
                      onClick={() => handleEditContact(contact)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary-blue)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span style={{ filter: 'brightness(0) invert(1)', display: 'flex' }}>
                        <EditIcon />
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteContact(contact)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-danger)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span style={{ filter: 'brightness(0) invert(1)', display: 'flex' }}>
                        <TrashCanIcon />
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Contact Modal */}
      <ContactFormModal
        isOpen={isContactFormOpen}
        contact={editingContact}
        onCancel={() => {
          setIsContactFormOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleContactFormSubmit}
      />

      {/* Delete Contact Modal */}
      <DeleteContactModal
        isOpen={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingContact(null);
        }}
        onConfirm={handleConfirmDeleteContact}
      />

      {/* Edit Brand Info Modal */}
      <EditBrandInfoModal
        isOpen={isEditBrandOpen}
        data={getBrandInfoData()}
        onCancel={() => setIsEditBrandOpen(false)}
        onSave={handleSaveBrandInfo}
      />

      {/* Delete Brand Modal */}
      <DeleteContactModal
        isOpen={isDeleteBrandOpen}
        title="DELETE BRAND"
        itemName="brand"
        onCancel={() => setIsDeleteBrandOpen(false)}
        onConfirm={handleConfirmDeleteBrand}
      />
    </div>
  );
};

export default BrandProfilePage;
